import { useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams, type Path } from 'react-router';
import { FileText } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';
import { Badge } from '@rocco/components/badge';
import { cn } from '@rocco/utils/cn';
import { Pagination } from '@rocco/ui/pagination/components/Pagination';
import { PaginationPageSizeControl } from '@rocco/ui/pagination/components/PaginationPageSizeControl';

import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import type { DownloadsPageLoader } from '@modules/professional/page/loaders/downloads-page-loader.server';
import {
    DOWNLOAD_ITEM_TYPE_LABELS,
    DOWNLOAD_GROUP_PAGE_SIZE_OPTIONS,
    DOWNLOAD_PAGE_PARAM,
    DOWNLOAD_PAGE_SIZE_PARAM,
    DOWNLOAD_SEARCH_PARAM,
    type DownloadItemType,
} from '@modules/professional/config/download-filters';
import { useProfessionalOverviewShared } from '@modules/professional/hook/use-professional-overview-shared';
import { useDownloadSearch } from '@modules/professional/hook/use-download-search';
import { TAB_CONTENT_GAP } from '../utils/tab-spacing';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { DownloadFilterSidebar } from './downloads/DownloadFilterSidebar';
import {
    DownloadGroupPanel,
    prefetchDownloadGroup,
} from './downloads/DownloadGroupPanel';
import {
    LookbookTiles,
    type LookbookTilesHandle,
} from './downloads/LookbookTiles';
import { ProfessionalTabListingBar } from './ProfessionalTabListingBar';
import {
    profileLookbook,
    showsProfileLookbook,
} from './downloads/profile-lookbook';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import { DownloadSurface } from 'generated/graphql';
import type { DownloadRowGate } from './downloads/DownloadRow';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';

/**
 * A filtered view narrow enough to read at a glance opens itself, so a search
 * lands on the files rather than on a heading the reader has to click.
 */
const AUTO_OPEN_MAX_GROUPS = 3;

type Props = DownloadsPageLoader;

/**
 * The professional's whole download library: the files on their profile page plus
 * everything hanging off their products, projects and articles.
 *
 * Laid out the way every other professional tab lays out a filtered list — filters
 * in the left drawer, search and sort on the right. Project lookbooks lead as cover
 * tiles; every other file follows as one accordion row per owning item, carrying a
 * count. The files inside a row are fetched when it opens, because a supplier can
 * own several thousand files and the list of headings has to stay cheap.
 */
export const ProfessionalDownloadsSection = ({
    urlSegment,
    filters,
    groups,
    lookbooks: projectLookbooks,
    fileCategories,
    itemTypeCounts,
    totalGroups,
    totalFiles,
}: Props) => {
    const { professionalOverview } = useProfessionalOverviewShared();

    // One gate for the whole tab. The lead a file earns is recorded once per
    // professional, so a gate per group would each hold their own "already
    // recorded" flag and file a lead for every group the reader opens. No
    // library item: the tab lists files from across the profile rather than
    // from one page, and a row that is a generated document names its own
    // surface on the attempt.
    const { isGated, isLoggedIn, handleGatedDownload, registerDownloadLead } =
        useGatedDownloads({
            surface: DownloadSurface.ProfileDownloads,
            professionalID: professionalOverview.ID,
        });
    const gate: DownloadRowGate = useMemo(
        () => ({
            surface: DownloadSurface.ProfileDownloads,
            isGated,
            isLoggedIn,
            onGatedDownload: handleGatedDownload,
            registerDownloadLead,
        }),
        [isGated, isLoggedIn, handleGatedDownload, registerDownloadLead],
    );
    const { desktop } = useAppDisplayModeContext();
    const [, setSearchParams] = useSearchParams();
    // Closed on every visit: the tab opens on the files, and a reader who wants
    // to narrow them opens the drawer.
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const lookbooksRef = useRef<LookbookTilesHandle>(null);
    const initial = useProfessionalInitialRouteData();
    const location = useLocation();
    const { pathname } = location;
    // The professional's own book leads the stack, ahead of the projects'.
    const lookbooks = useMemo(
        () =>
            initial && showsProfileLookbook(filters, initial.Title)
                ? [
                      profileLookbook(
                          initial,
                          professionalTabBase(initial.URLSegment, pathname),
                      ),
                      ...projectLookbooks,
                  ]
                : projectLookbooks,
        [initial, filters, pathname, projectLookbooks],
    );
    const [lookbooksExpanded, setLookbooksExpanded] = useState(false);

    const hasFilters =
        !!filters.search ||
        filters.itemTypes.length > 0 ||
        filters.fileCategories.length > 0;

    const defaultOpen = useMemo(() => {
        if (!hasFilters || totalGroups > AUTO_OPEN_MAX_GROUPS) return [];
        return groups.map((group) => String(group.ItemID));
    }, [hasFilters, totalGroups, groups]);

    // Remounting the accordion when the filters change is what lets a narrowed
    // view open itself; one instance would hold the reader's previous open rows
    // against the new result.
    const accordionKey = `${filters.search}|${filters.itemTypes.join(',')}|${filters.fileCategories.join(',')}|${filters.sort}|${filters.page}`;

    // Whether any file this professional owns is categorised at all. When none is,
    // the File column is a reserved void on every row, so it is dropped for the
    // whole tab rather than per row.
    const showFileColumn = fileCategories.length > 0;

    const typeCounts = useMemo(
        () =>
            Object.fromEntries(
                itemTypeCounts.map((entry) => [
                    entry.ItemType,
                    entry.FileCount,
                ]),
            ) as Partial<Record<DownloadItemType, number>>,
        [itemTypeCounts],
    );

    const setParam = (name: string, value: string) => {
        setSearchParams(
            (params) => {
                if (value) {
                    params.set(name, value);
                } else {
                    params.delete(name);
                }
                params.delete(DOWNLOAD_PAGE_PARAM);
                return params;
            },
            { preventScrollReset: true },
        );
    };

    const { keyword, setKeyword } = useDownloadSearch(
        filters.search,
        (search) => setParam(DOWNLOAD_SEARCH_PARAM, search),
    );

    // The page links keep every other param, so a page of a filtered view is
    // still that view; page one drops the param rather than carrying `page=1`.
    const pageUrl = (path: Path, page: number) => {
        const params = new URLSearchParams(path.search);
        if (page <= 1) {
            params.delete(DOWNLOAD_PAGE_PARAM);
        } else {
            params.set(DOWNLOAD_PAGE_PARAM, String(page));
        }
        const query = params.toString();
        return `${path.pathname}${query ? `?${query}` : ''}`;
    };
    const totalPages = Math.max(1, Math.ceil(totalGroups / filters.pageSize));
    // A new length starts the list again: the reader's page number counted
    // pages of the old length.
    const setPageSize = (pageSize: number) => {
        setSearchParams(
            (params) => {
                params.set(DOWNLOAD_PAGE_SIZE_PARAM, String(pageSize));
                params.delete(DOWNLOAD_PAGE_PARAM);
                return params;
            },
            { preventScrollReset: true },
        );
    };
    const listTopRef = useRef<HTMLDivElement>(null);

    // Grey page, white rows. A list of white cards on a white page had nothing
    // separating one file from the next.
    return (
        <section className="w-full overflow-x-clip bg-ground text-[#1f1f21]">
            {/* The tab pill above already names the page, so the heading is
                kept for the document outline only. */}
            {desktop && (
                <div
                    className={cn(
                        'mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem]',
                        TAB_CONTENT_GAP,
                    )}
                >
                    <h2 className="sr-only">Downloads</h2>
                </div>
            )}
            {desktop && (
                <div className="mx-auto w-full max-w-(--ap-page-width) md:px-[3.132rem] md:pt-[2.8rem]">
                    <div className="h-px w-full bg-black/15" />
                </div>
            )}

            {/* Where a page link lands the reader: the top of the list. */}
            <div
                ref={listTopRef}
                className="mx-auto w-full max-w-(--ap-page-width) scroll-mt-[6rem] max-md:px-6 max-md:pt-3 max-md:pb-14 md:px-[3.132rem] md:pt-[1.5rem] md:pb-[7.5rem]"
            >
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    contentClassName="min-w-0"
                    sidePanel={
                        desktop && (
                            <div className="h-full md:pt-[1.8rem]">
                                <DownloadFilterSidebar
                                    itemTypes={
                                        filters.itemTypes as DownloadItemType[]
                                    }
                                    fileCategories={filters.fileCategories}
                                    availableCategories={fileCategories}
                                    itemTypeCounts={typeCounts}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    {/* The listing tabs' bar. No Display at its end: the tab
                        lists lookbooks and file rows, not a grid of columns. */}
                    <ProfessionalTabListingBar
                        search={{
                            value: keyword,
                            onChange: setKeyword,
                            placeholder: 'Search Downloads',
                            ariaLabel: 'Search files',
                        }}
                        filterDrawer={{
                            open: filterDrawerOpen,
                            onToggle: () =>
                                setFilterDrawerOpen((open) => !open),
                        }}
                    />

                    {lookbooks.length > 0 && (
                        <LookbookTiles
                            ref={lookbooksRef}
                            onExpandedChange={setLookbooksExpanded}
                            lookbooks={lookbooks}
                            professionalID={professionalOverview.ID}
                            professionalTitle={professionalOverview.Title}
                        />
                    )}

                    {lookbooks.length > 1 && (
                        // The same control the product page's downloads use,
                        // in the same place: under what it reveals.
                        <LoadMoreButton
                            onClick={() => lookbooksRef.current?.toggle()}
                            label={
                                lookbooksExpanded
                                    ? 'Stack lookbooks'
                                    : `Show all ${lookbooks.length}`
                            }
                            expanded={lookbooksExpanded}
                            className="pt-[2.5rem]"
                        />
                    )}

                    {lookbooks.length > 0 && groups.length > 0 && (
                        <h3 className="pt-12 font-sans text-lg font-medium text-[#141414]">
                            Files
                        </h3>
                    )}

                    {groups.length > 0 && (
                        <p
                            className={cn(
                                'font-sans text-base text-[#666666]',
                                lookbooks.length > 0 ? 'pt-1' : 'pt-4',
                            )}
                            role="status"
                        >
                            {totalFiles} {totalFiles === 1 ? 'file' : 'files'}{' '}
                            across {totalGroups}{' '}
                            {totalGroups === 1 ? 'item' : 'items'}
                        </p>
                    )}

                    {groups.length === 0 ? (
                        lookbooks.length === 0 && (
                            <p className="py-10 font-sans text-lg text-[#666666]">
                                No files match your search.
                            </p>
                        )
                    ) : (
                        <Accordion
                            key={accordionKey}
                            type="multiple"
                            defaultValue={defaultOpen}
                            className="flex flex-col gap-[0.375rem] pt-6"
                        >
                            {groups.map((group) => (
                                <AccordionItem
                                    key={group.ItemID}
                                    value={String(group.ItemID)}
                                    variant="noBorder"
                                    className="overflow-hidden rounded-lg bg-white inset-ring-1 inset-ring-black/10"
                                >
                                    <AccordionTrigger
                                        className="min-w-0 gap-4 px-4 py-3 text-left max-md:gap-3 max-md:px-3"
                                        headingLevel={3}
                                        // Warm the group before it is asked for,
                                        // so opening it lands on rows rather
                                        // than on a skeleton.
                                        onMouseEnter={() =>
                                            void prefetchDownloadGroup({
                                                urlSegment,
                                                itemID: group.ItemID,
                                                search: filters.search,
                                                fileCategories:
                                                    filters.fileCategories,
                                            })
                                        }
                                        onFocus={() =>
                                            void prefetchDownloadGroup({
                                                urlSegment,
                                                itemID: group.ItemID,
                                                search: filters.search,
                                                fileCategories:
                                                    filters.fileCategories,
                                            })
                                        }
                                    >
                                        <span className="flex min-w-0 flex-1 items-center gap-4 max-md:gap-3">
                                            <GroupThumbnail
                                                src={group.ThumbnailUrl}
                                                alt=""
                                            />
                                            <span className="flex min-w-0 flex-col gap-1.5">
                                                {/* Wraps on a phone, where a product
                                                    name runs past the card; one
                                                    line on desktop. */}
                                                <span className="font-sans font-medium text-[#141414] max-md:text-[0.9375rem] max-md:leading-[1.25] max-md:[overflow-wrap:anywhere] md:truncate md:text-lg">
                                                    {group.Title}
                                                </span>
                                                <span className="flex items-center gap-2">
                                                    <Badge
                                                        color="secondary"
                                                        size="narrow"
                                                    >
                                                        {
                                                            DOWNLOAD_ITEM_TYPE_LABELS[
                                                                group.ItemType as DownloadItemType
                                                            ]
                                                        }
                                                    </Badge>
                                                    <span className="font-sans text-[#666666] max-md:text-sm md:text-base">
                                                        {group.FileCount}{' '}
                                                        {group.FileCount === 1
                                                            ? 'file'
                                                            : 'files'}
                                                    </span>
                                                </span>
                                            </span>
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-4">
                                        <DownloadGroupPanel
                                            urlSegment={urlSegment}
                                            itemID={group.ItemID}
                                            fileCount={group.FileCount}
                                            search={filters.search}
                                            fileCategories={
                                                filters.fileCategories
                                            }
                                            professionalID={
                                                professionalOverview.ID
                                            }
                                            professionalTitle={
                                                professionalOverview.Title
                                            }
                                            showFileColumn={showFileColumn}
                                            gate={gate}
                                        />
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}

                    {totalGroups > 0 ? (
                        // The shared pagination: where the reader is in the list, how many

                        // pages it runs to, and how long a page is.

                        <div className="flex flex-col items-center gap-6 pt-8 md:flex-row md:justify-between">
                            <PaginationPageSizeControl
                                currentPage={filters.page}
                                pageSize={filters.pageSize}
                                totalCount={totalGroups}
                                itemLabel="items"
                                pageSizeOptions={
                                    DOWNLOAD_GROUP_PAGE_SIZE_OPTIONS
                                }
                                onPageSizeChange={setPageSize}
                            />

                            {totalPages > 1 ? (
                                <Pagination
                                    className="mx-0 w-auto"
                                    currentPath={location}
                                    current={Math.min(filters.page, totalPages)}
                                    total={totalPages}
                                    urlBuilder={pageUrl}
                                    scrollToRef={listTopRef}
                                    visibleCount={desktop ? 4 : 3}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};

/**
 * The item's picture, or its glyph. Items whose thumbnail has never been
 * generated show the glyph rather than making the page wait on one.
 */
const GroupThumbnail = ({ src, alt }: { src?: string | null; alt: string }) =>
    src ? (
        <img
            src={src}
            alt={alt}
            loading="lazy"
            className="size-[3.75rem] shrink-0 rounded object-cover"
        />
    ) : (
        <span className="flex size-[3.75rem] shrink-0 items-center justify-center rounded bg-[#f4f4f5]">
            <FileText
                className="size-5 text-[#888888]"
                strokeWidth={1.5}
                aria-hidden
            />
        </span>
    );
